import { useEffect, useRef } from 'react';
import { IconCrown, IconKey, IconLock, IconShield, IconX } from './icons';
import { RegionFlag } from './RegionFlag';
import { useAnimatedModal } from '../hooks/useAnimatedModal';
import type { PortalRegionItem } from '../services/api';
import { subStatusLabel, tierLabel } from '../utils/format';

export type CreateKeyProtocol = 'wg' | 'ovpn';

interface CreateKeyModalProps {
  open: boolean;
  onClose: () => void;
  protocol: CreateKeyProtocol;
  onProtocolChange: (protocol: CreateKeyProtocol) => void;
  region: string;
  onRegionChange: (code: string) => void;
  regions: PortalRegionItem[];
  wgServers: string[];
  ovpnServers: string[];
  subscriptionTier?: string;
  subscriptionStatus?: string;
  subscriptionUntilLabel?: string;
  maxPerRegion: number;
  maxOvpnKeys: number;
  wgKeysOnRegion: number;
  ovpnKeysCount: number;
  busy: boolean;
  error: string | null;
  onCreate: () => void;
}

function regionMeta(regions: PortalRegionItem[], code: string): PortalRegionItem | undefined {
  return regions.find((r) => r.code.toLowerCase() === code.toLowerCase());
}

export function CreateKeyModal({
  open,
  onClose,
  protocol,
  onProtocolChange,
  region,
  onRegionChange,
  regions,
  wgServers,
  ovpnServers,
  subscriptionTier = 'default',
  subscriptionStatus = 'none',
  subscriptionUntilLabel,
  maxPerRegion,
  maxOvpnKeys,
  wgKeysOnRegion,
  ovpnKeysCount,
  busy,
  error,
  onCreate,
}: CreateKeyModalProps) {
  const ui = useAnimatedModal(open);
  const backdropDownRef = useRef(false);
  const servers = protocol === 'wg' ? wgServers : ovpnServers;
  const atLimit =
    protocol === 'wg'
      ? Boolean(region) && wgKeysOnRegion >= maxPerRegion
      : ovpnKeysCount >= maxOvpnKeys;
  const tier = (subscriptionTier || 'default').toLowerCase();
  const isPremium = tier === 'premium';

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  useEffect(() => {
    if (!open) return;
    if (servers.length === 0) return;
    if (!servers.some((s) => s.toLowerCase() === region.toLowerCase())) {
      onRegionChange(servers[0]!);
    }
  }, [open, protocol, servers, region, onRegionChange]);

  if (!ui.mounted) return null;

  const selected = regionMeta(regions, region);

  return (
    <div
      className={`modal-backdrop create-key-backdrop${ui.visible ? ' is-open' : ''}`}
      role="presentation"
      onMouseDown={(e) => {
        backdropDownRef.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (!busy && backdropDownRef.current && e.target === e.currentTarget) onClose();
        backdropDownRef.current = false;
      }}
    >
      <div
        className="modal-sheet create-key-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-key-title"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="create-key-head">
          <div>
            <h3 id="create-key-title">Создать ключ</h3>
            <p className="muted create-key-sub">Выберите протокол и регион сервера</p>
          </div>
          <button type="button" className="create-key-close" onClick={onClose} disabled={busy} aria-label="Закрыть">
            <IconX size={18} />
          </button>
        </div>

        <div className={`create-key-tier ${isPremium ? 'is-premium' : 'is-default'}`}>
          <span className="create-key-tier-icon">
            {isPremium ? <IconCrown size={18} /> : <IconKey size={18} />}
          </span>
          <div className="create-key-tier-copy">
            <strong>{tierLabel(tier)}</strong>
            <span>
              {subStatusLabel(subscriptionStatus)}
              {subscriptionUntilLabel ? ` · до ${subscriptionUntilLabel}` : ''}
            </span>
          </div>
          <div className="create-key-tier-limits">
            {protocol === 'wg' ? (
              <>
                <strong>
                  {wgKeysOnRegion}/{maxPerRegion}
                </strong>
                <span>на регион</span>
              </>
            ) : (
              <>
                <strong>
                  {ovpnKeysCount}/{maxOvpnKeys}
                </strong>
                <span>на аккаунт</span>
              </>
            )}
          </div>
        </div>

        <div className="create-key-section">
          <div className="create-key-label">Протокол</div>
          <div className="create-key-protocols">
            <button
              type="button"
              className={`create-key-proto ${protocol === 'wg' ? 'is-active' : ''}`}
              onClick={() => onProtocolChange('wg')}
              disabled={busy || wgServers.length === 0}
            >
              <span className="create-key-proto-icon wg">
                <IconShield size={18} />
              </span>
              <span>
                <strong>WireGuard</strong>
                <span className="muted">до {maxPerRegion} на регион</span>
              </span>
            </button>
            <button
              type="button"
              className={`create-key-proto ${protocol === 'ovpn' ? 'is-active' : ''}`}
              onClick={() => onProtocolChange('ovpn')}
              disabled={busy || ovpnServers.length === 0}
            >
              <span className="create-key-proto-icon ovpn">
                <IconLock size={18} />
              </span>
              <span>
                <strong>OpenVPN</strong>
                <span className="muted">до {maxOvpnKeys} на аккаунт</span>
              </span>
            </button>
          </div>
        </div>

        <div className="create-key-section">
          <div className="create-key-label">Регион</div>
          {servers.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              Нет доступных серверов для тарифа {tierLabel(tier)}.
            </p>
          ) : (
            <div className="create-key-regions">
              {servers.map((code) => {
                const meta = regionMeta(regions, code);
                const active = region.toLowerCase() === code.toLowerCase();
                return (
                  <button
                    key={code}
                    type="button"
                    className={`create-key-region ${active ? 'is-active' : ''}`}
                    onClick={() => onRegionChange(code)}
                    disabled={busy}
                  >
                    <RegionFlag code={code} label={meta?.name || code} size={32} />
                    <span className="create-key-region-copy">
                      <strong>{meta?.name || code.toUpperCase()}</strong>
                      <span className="muted">
                        {meta?.city || code.toUpperCase()}
                        {meta?.protocols?.length
                          ? ` · ${meta.protocols.map((p) => p.toUpperCase()).join(', ')}`
                          : ''}
                      </span>
                    </span>
                    {active ? <span className="create-key-region-check" aria-hidden>✓</span> : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {selected ? (
          <p className="create-key-hint muted">
            Выбрано: {selected.name}
            {selected.city ? ` (${selected.city})` : ''} · {protocol === 'wg' ? 'WireGuard' : 'OpenVPN'}
          </p>
        ) : null}

        {error ? <p className="error-text create-key-error">{error}</p> : null}
        {atLimit ? (
          <p className="error-text create-key-error">
            {protocol === 'wg'
              ? `Лимит WireGuard на регион: ${maxPerRegion} (тариф ${tierLabel(tier)}).`
              : `Лимит OpenVPN: ${maxOvpnKeys} на аккаунт (тариф ${tierLabel(tier)}).`}
          </p>
        ) : null}

        <div className="create-key-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !region || servers.length === 0 || atLimit}
            onClick={onCreate}
          >
            {busy ? 'Создание…' : 'Создать ключ'}
          </button>
        </div>
      </div>
    </div>
  );
}
